/**
 * ScheduleGenerator.ts — Balanced schedule for 24 teams, 160 games.
 * April 1 (Day 1) - Sept 30 (Day 183)
 * Playoffs: Oct 1 - Oct 31 (Day 184 - 215)
 */
export interface ScheduledGame {
  id: string;
  gameDay: number;
  homeTeamId: string;
  awayTeamId: string;
  isPlayoff?: boolean;
  seriesId?: number;
  gameNumber?: number;
}

export interface TeamScheduleInfo {
  teamId: string;
  conference: 'American' | 'National';
  division: 'East' | 'West';
  position: number;
}

export function annotateSeriesGames(games: ScheduledGame[]): ScheduledGame[] {
  const tracker = new Map<string, number>();
  return games.map((game) => {
    const key = `${game.homeTeamId}-${game.awayTeamId}-${game.seriesId ?? 'reg'}`;
    const seq = (tracker.get(key) || 0) + 1;
    tracker.set(key, seq);
    return { ...game, gameNumber: game.gameNumber ?? seq };
  });
}

import { generateMasterSchedule } from './MasterScheduleBuilder';

export function generateSeasonSchedule(
  teams: any[],
  leagueId: string,
  teamConfs?: Map<string, { conference: string; division: string }>
): ScheduledGame[] {
  console.log(`🎯 MLB SCHEDULE GENERATOR for ${teams.length} teams`);
  
  // Convert teams to TeamScheduleInfo format
  const teamScheduleInfos: TeamScheduleInfo[] = teams.map((team, index) => ({
    teamId: team?.teamId || team?.id,
    conference: team?.conference || (index < 12 ? 'American' : 'National'),
    division: team?.division || (index % 2 === 0 ? 'East' : 'West'),
    position: index + 1
  })).filter(t => t.teamId); // Filter out any without IDs
  
  if (teamScheduleInfos.length < 2) {
    console.warn(`⚠️ Need at least 2 valid teams, got ${teamScheduleInfos.length}`);
    return [];
  }
  
  console.log(`🔍 First team:`, teamScheduleInfos[0]);
  
  // Use the MASTER SCHEDULE built from real Basehit team schedules
  return generateMasterSchedule(teamScheduleInfos, leagueId, 0);
}

export function generatePlayoffs(leagueId: string, teamIds: string[]): ScheduledGame[] {
  const playoffGames: ScheduledGame[] = [];
  let day = 184;
  let gameId = 0;
  for (let i = 0; i < teamIds.length - 1; i += 2) {
    for (let g = 0; g < 7; g++) {
      playoffGames.push({
        id: `po-1-${gameId++}`,
        gameDay: day + g,
        homeTeamId: g % 2 === 0 ? teamIds[i] : teamIds[i+1],
        awayTeamId: g % 2 === 0 ? teamIds[i+1] : teamIds[i],
        isPlayoff: true,
        seriesId: Math.floor(i / 2),
        gameNumber: g + 1
      });
    }
  }
  return playoffGames;
}

const AI_TEAM_NAMES = ['Bombers','Blazers','Bandits','Bears','Bulls','Comets','Cyclones','Crusaders','Cougars','Demons','Dragons','Dukes','Eagles','Empire','Falcons','Fire','Flash','Foxes','Giants','Grizzlies','Hawks','Heroes','Lions','Legends','Marauders','Mavericks','Monarchs','Mustangs','Panthers','Pirates','Phoenix','Raiders','Rangers','Raptors','Rebels','Rockets','Sharks','Spartans','Stars','Storm','Thunder','Tigers','Titans','Vikings','Vipers','Voyagers','Warriors','Wolves','Wizards','Wildcats'];
const AI_TEAM_CITIES = ['New York','Boston','Los Angeles','Chicago','Houston','Phoenix','Philadelphia','San Diego','Dallas','Austin','Miami','Seattle','Denver','Atlanta','San Francisco','Minneapolis','Detroit','Cleveland','Pittsburgh','Milwaukee'];

export function generateAITeam(tier: number): { name: string; city: string; abbreviation: string } {
  const name = AI_TEAM_NAMES[Math.floor(Math.random() * AI_TEAM_NAMES.length)];
  const city = AI_TEAM_CITIES[Math.floor(Math.random() * AI_TEAM_CITIES.length)];
  return { name, city, abbreviation: city.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 3) };
}

export function assignTeamsToConferences(teams: any[]): TeamScheduleInfo[] {
  console.log(`🎯 Assigning ${teams.length} teams to conferences/divisions`);
  
  // Shuffle teams for random distribution
  const shuffled = [...teams].sort(() => Math.random() - 0.5);
  const assignments: TeamScheduleInfo[] = [];
  
  // We need exactly 24 teams for proper distribution
  const targetTeams = 24;
  const teamsToUse = shuffled.slice(0, targetTeams);
  
  // 6 teams per division (4 divisions total: American-East, American-West, National-East, National-West)
  const teamsPerDivision = 6;
  const confs = ['American', 'National'] as const;
  const divs = ['East', 'West'] as const;
  
  let teamIndex = 0;
  let position = 1;
  
  for (const conf of confs) {
    for (const div of divs) {
      for (let i = 0; i < teamsPerDivision; i++) {
        if (teamIndex < teamsToUse.length) {
          const team = teamsToUse[teamIndex];
          assignments.push({
            teamId: team.id,
            conference: conf,
            division: div,
            position: position++
          });
          teamIndex++;
        } else {
          console.warn(`⚠️ Not enough teams for division ${conf}-${div}`);
        }
      }
    }
  }
  
  console.log(`✅ Assigned ${assignments.length} teams to conferences/divisions`);
  console.log(`📊 Distribution: ${confs.map(c => divs.map(d => 
    `${c}-${d}: ${assignments.filter(a => a.conference === c && a.division === d).length}`
  ).join(', ')).join('; ')}`);
  
  return assignments;
}